import React, { useContext, useEffect, useState } from "react";
import { MyContext } from "../../../context/MyContext";
import socratesApi from "../../../api/socratesApi";
import AirTitle from "../../../components/AirTitle";
import { AiLine, Chevron } from "../../../assets/svgs/icons";

interface InterviewRaw {
  id?: number;
  interviewId?: number;
  interview_id?: number;
  title?: string;
  interviewType?: string;
  interview_type?: string;
  interviewSubject?: string;
  interview_subject?: string;
  targetInstitution?: string;
  target_institution?: string;
  keywords?: string[] | string;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  status?: string;
}

interface Interview {
  id: number;
  title?: string;
  interviewType?: string;
  interviewSubject?: string;
  targetInstitution?: string;
  keywords?: string[] | string;
  createdAt?: string;
  updatedAt?: string;
  status?: string;
}

interface SocratesInterviewListProps {
  onStartInterview: (interviewId: number) => void;
}

/**
 * SocratesInterviewList
 * 사용자의 소크라테스 면접 목록을 카드 형식으로 표시
 * 면접이 없을 경우, "소크라테스가 처음이신가요?" 첫 이용 화면 표시
 * CTA 버튼은 부모 컴포넌트(SocratesV2)의 푸터에서 렌더링
 */
const SocratesInterviewList: React.FC<SocratesInterviewListProps> = ({
  onStartInterview,
}) => {
  const { user } = useContext(MyContext);
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [loading, setLoading] = useState(false);

  // 면접 목록 로드
  useEffect(() => {
    const loadInterviews = async () => {
      if (!user?.id) return;

      setLoading(true);
      try {
        const result = await socratesApi.getUserInterviews(user.id);

        if (result) {
          const rawInterviews: InterviewRaw[] = Array.isArray(result)
            ? result
            : (result as any).interviews || [];

          // API 응답 필드를 정규화
          const normalizedInterviews: Interview[] = rawInterviews
            .map((item) => ({
              id: item.id || item.interviewId || item.interview_id || 0,
              title: item.title,
              interviewType: item.interviewType || item.interview_type,
              interviewSubject: item.interviewSubject || item.interview_subject,
              targetInstitution:
                item.targetInstitution || item.target_institution,
              keywords: item.keywords,
              createdAt: item.createdAt || item.created_at,
              updatedAt: item.updatedAt || item.updated_at,
              status: item.status,
            }))
            .filter((item) => item.id !== 0);

          setInterviews(normalizedInterviews);
        }
      } catch (error) {
        console.error("면접 목록 로드 실패:", error);
      } finally {
        setLoading(false);
      }
    };

    loadInterviews();
  }, [user?.id]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return `생성일 ${date.getFullYear()}.${String(
        date.getMonth() + 1
      ).padStart(2, "0")}.${String(date.getDate()).padStart(2, "0")}`;
    } catch {
      return "";
    }
  };

  // 카드 제목 생성: "관심영역 소크라테스" 형식
  const getCardTitle = (interview: Interview) => {
    if (interview.interviewSubject) {
      return `${interview.interviewSubject} 소크라테스`;
    }
    if (interview.title) {
      return interview.title;
    }
    return "소크라테스";
  };

  // 카드 정보 항목들 생성
  const getCardInfoItems = (interview: Interview): string[] => {
    const items: string[] = [];

    // 면접유형
    if (interview.interviewType) {
      items.push(interview.interviewType);
    }

    // 기관명
    if (interview.targetInstitution) {
      items.push(interview.targetInstitution);
    }

    // 관심영역
    if (interview.interviewSubject) {
      items.push(interview.interviewSubject);
    }

    // 관심주제 (keywords)
    if (interview.keywords) {
      if (Array.isArray(interview.keywords)) {
        items.push(interview.keywords.join(", "));
      } else if (typeof interview.keywords === "string") {
        items.push(interview.keywords);
      }
    }

    return items;
  };

  // 면접이 없을 때: 첫 이용 화면 표시 (버튼은 푸터에서 렌더링)
  if (!loading && interviews.length === 0) {
    return (
      <div className="flex flex-col items-center gap-8 w-full">
        <AirTitle
          mode="socrates"
          title="소크라테스가 처음이신가요?"
          subtitle={`자기소개서, 탐구보고서, 수업 내용도 OK!\n입력만하면 소크라테스가 질문을 시작합니다.`}
        />
      </div>
    );
  }

  // 면접이 있을 때: 카드 목록 표시
  return (
    <div className="flex flex-col items-center gap-8 w-full">
      {/* 타이틀 */}
      <AirTitle
        mode="socrates"
        title="모의 면접을 시작할까요?"
        subtitle={`자기소개서, 탐구보고서, 수업 내용도 OK!\n입력만 하면 소크라테스가 질문을 시작합니다.`}
      />

      {/* 면접 카드 그리드 - 최근 4개만 표시 */}
      {!loading && interviews.length > 0 && (
        <div className="flex flex-wrap justify-center gap-4 w-full max-w-[800px] min-w-[312px]">
          {interviews
            .slice(interviews.length - 4, interviews.length)
            .reverse()
            .map((interview) => {
              const infoItems = getCardInfoItems(interview);

              return (
                <div
                  key={interview.id}
                  className="border border-solid border-[#e2d5fc] rounded-[20px] p-4 flex flex-col gap-4 min-w-[312px] w-[312px] bg-white hover:shadow-lg transition-shadow"
                >
                  {/* 카드 헤더: 제목 + 면접시작 버튼 */}
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-1">
                      <AiLine className="w-5 h-5 text-[#6d2ef2]" />
                      <span className="font-bold text-[16px] text-[#212121] leading-[20px] tracking-[-0.32px]">
                        {getCardTitle(interview)}
                      </span>
                    </div>
                    <button
                      onClick={() => onStartInterview(interview.id)}
                      className="flex items-center justify-center h-8 rounded overflow-hidden"
                    >
                      <span className="text-[14px] font-medium text-[#6d2ef2] underline tracking-[-0.28px] leading-[20px]">
                        면접시작
                      </span>
                      <Chevron className="w-5 h-5 fill-[#6d2ef2]" />
                    </button>
                  </div>

                  {/* 카드 정보: 면접유형 | 기관명 | 관심영역 | 관심주제 */}
                  <div className="flex flex-wrap items-center gap-1">
                    {infoItems.map((item, index) => (
                      <React.Fragment key={index}>
                        <span className="text-[14px] font-medium text-[#212121] tracking-[-0.28px] leading-[20px]">
                          {item}
                        </span>
                        {index < infoItems.length - 1 && (
                          <span className="w-px h-3 bg-[#ccc]" />
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* 생성일 */}
                  <div className="text-[12px] text-[#888] text-right tracking-[-0.24px] leading-[16px] w-full">
                    {formatDate(interview.createdAt || interview.updatedAt)}
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {/* 로딩 중 */}
      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6d2ef2] mx-auto"></div>
        </div>
      )}
    </div>
  );
};

export default SocratesInterviewList;
